import { hasFeature } from 'root/api/admin/features';

const fetchPaymentsGatewayName = async ({ accountID, shopID }) => {
  const res = await $.get(`/API/Account/${accountID}/Shop/${shopID}.json`);

  const { gatewayConfigID } = res.Shop;
  if (!gatewayConfigID) {
    return '';
  }

  const gwRes = await $.get(`/API/Account/${accountID}/GatewayConfig/${gatewayConfigID}.json`);
  const { gatewayID } = gwRes.GatewayConfig; // i.e.: "cayan" or "lightspeed-payments"
  return gatewayID;
};

const fetchHasEcom = async ({ accountID }) => {
  const res = await $.get(`/API/Account/${accountID}/Integration.json`);

  let integrations = res.Integration || [];
  if (!Array.isArray(integrations)) {
    integrations = [integrations];
  }

  const integration = integrations.find(
    ({ systemIntegrationID }) => systemIntegrationID === '50' || systemIntegrationID === '55'
  );
  return !!integration;
};

const logFrontendEvent = (data) => {
  const formData = new FormData();
  formData.append('message', `[INTERCOM_FRONTEND_LOG]: ${JSON.stringify(data)}`);

  $.ajax({
    type: 'POST',
    url: '/system/frontendLogger',
    data: formData,
    contentType: false,
    processData: false,
    cache: false,
    error: () => {
      // eslint-disable-next-line no-console
      console.error('Failed to log frontend event');
    },
  });
};

class Intercom {
  init() {
    // Do not load intercom if it is already loaded via the Chrome extension, or in Stencil
    if (window.Intercom || window.isGriffin()) {
      return;
    }
    const getModule = (moduleName) => window.lsCloud.Modules.hasModule(moduleName);

    const logEvents = getModule('intercom.frontend_logging');

    // Set Intercom App ID based on environment
    const hostname = window.location.hostname;
    const isProd = /merchantos\.com/.test(hostname) || /lightspeedapp\.com/.test(hostname);

    const appID = isProd ? 'wk5n0598' : 'spztd9ih';

    // Intercom initialization script
    /* eslint-disable */
    // prettier-ignore
    (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/' + appID;var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
    /* eslint-enable */
    window.Intercom('shutdown');

    // Bootstrap Intercom
    window.merchantos.session.init().done(async () => {
      const session = window.merchantos.session.sessData;
      const isImpersonator = Object.prototype.hasOwnProperty.call(session, 'Impersonator');
      if (isImpersonator) {
        return;
      }

      const {
        systemCustomerID: accountID,
        systemCustomerName: accountName,
        shopCount,
        systemUserLogin,
      } = session;
      const { firstName, lastName, email, employeeID } = session.Employee;
      const { shopID, name: shopName } = session.Shop;

      const [hasEcom, gatewayName] = await Promise.all([
        fetchHasEcom({ accountID }),
        fetchPaymentsGatewayName({ accountID, shopID }),
      ]);

      // Determine placement and visuals of Intercom on page
      const intercomLauncher = {
        hide_default_launcher: true,
        vertical_padding: 20,
      };

      const userHasValidEmail = window.isEmail(systemUserLogin) || window.isEmail(email);
      const validEmail = window.isEmail(systemUserLogin) ? systemUserLogin : email;

      const userHash = await window.lsCloud.Config.access('intercom_user_hash');

      // User details to send to Intercom
      const intercomUser =
        userHasValidEmail && userHash
          ? {
              email: validEmail,
              name: `${firstName} ${lastName}`,
              user_hash: userHash,
              r_user_id: `${accountID}-${employeeID}`,
              r_employee_id: employeeID,
              r_shop_id: shopID,
              r_shop_name: shopName,
              r_owner: window.merchantos.session.hasRight('owner'),
              r_admin: window.merchantos.session.hasRight('admin'),
              r_isGriffin: window.isGriffin(),
            }
          : {};

      // Company information to send to Intercom
      const intercomCompany = {
        id: `r-${accountID}`,
        name: accountName,
        rad_id: accountID,
        r_location_count: shopCount,
        r_module_analytics: getModule('reporting.analytics.autologin'),
        r_module_negative_inventory: getModule('inventory.negative_inventory'),
        r_module_advanced_reporting: getModule('advanced_reporting_cloud'),
        r_module_settings_register_rules: getModule('settings.register_rules'),
        r_module_inventory_vendor_catalog_v2: getModule('inventory.vendor_catalog.v2'),
        r_module_gift_cards: getModule('omni.giftcards'),
        r_has_ecom: hasEcom,
        r_has_integrated_payments: !!gatewayName,
        r_payments_gateway: gatewayName,
        r_accountStatus: window.merchantos.account.status,
      };

      if (await hasFeature('SupportUniversalSupportCode')) {
        const uniqueSubscriptionIdentifier = await window.lsCloud.Options.get(
          'unique_subscription_identifier'
        );

        if (uniqueSubscriptionIdentifier) {
          intercomCompany.unique_subscription_id = uniqueSubscriptionIdentifier;
        }
      }

      // Intercom settings
      window.intercomSettings = {
        app_id: appID,
        custom_launcher_selector: '#help-modal-livechat-available',
        ...intercomLauncher,
        ...intercomUser,
        company: {
          ...intercomCompany,
        },
      };
      if (logEvents) {
        logFrontendEvent(window.intercomSettings.company);
      }

      // Bootstrap Intercom
      // Note: For SPA updates, see `router.js`
      window.Intercom('boot', window.intercomSettings);
      window.Intercom('update');

      // Intercom only allows 20 update call every 30 min
      // +2sec to ensure we call update right after the the limit refresh
      const safeUpdateRate = (1000 * 60 * 30) / 20 + 2000;
      setInterval(() => {
        if (logEvents) {
          logFrontendEvent(window.intercomSettings.company);
        }
        window.Intercom('update');
      }, safeUpdateRate);
    });
  }

  showSurvey(surveyId) {
    window?.Intercom('startSurvey', surveyId);
  }
}

export default new Intercom();
